import { QueryFunctionContext } from '@tanstack/react-query'
import { commonResponse } from '../modules/Auth/types/APIResponseType'
import { generalAPIRoutes } from './apiRoutes'
import anklePatrolAxios from './config/axiosClient'
import {
  AdminImposedCheckInPayload,
  BlockConsumer,
  FetchAdminRandomCheckInPayload,
} from './types/APIRequestType'
import {
  ApproveBabysitterPayload,
  BlockBabysitterPayload,
  ConsumersPayload,
  GlobalAdminDashboardPayload,
} from './types/APIRequestType'
import {
  AdminRandomCheckInButtonStatusResponse,
  ConsumersDetails,
  ConsumerUser,
  FetchAdminRandomCheckInResponse,
  GlobalAdminDashboardResponse,
  RegisteredBabysittersResponse,
  registeredConsumersCountResponse,
  registeredConsumersForBabysitterIdResponse,
} from './types/APIResponseType'

export const globalAdminDashboard = (
  payload: GlobalAdminDashboardPayload,
): Promise<commonResponse<GlobalAdminDashboardResponse>> =>
  anklePatrolAxios.post(generalAPIRoutes.globalAdminDashboard, payload)

export const registeredBabysitters = async ({
  queryKey,
}: QueryFunctionContext): Promise<
  commonResponse<RegisteredBabysittersResponse>
> => {
  const response: commonResponse<RegisteredBabysittersResponse> =
    await anklePatrolAxios.get(`${generalAPIRoutes.registeredBabysitters}`, {
      params: queryKey[1],
    })
  return response
}

export const blockBabysitter = (
  payload: BlockBabysitterPayload,
): Promise<commonResponse<null>> =>
  anklePatrolAxios.post(generalAPIRoutes.blockBabysitter, payload)

export const approveBabysitter = (
  payload: ApproveBabysitterPayload,
): Promise<commonResponse<null>> =>
  anklePatrolAxios.put(generalAPIRoutes.approveBabysitter, payload)

export const registeredConsumersCount = (payload: {
  userId: string
}): Promise<commonResponse<registeredConsumersCountResponse>> =>
  anklePatrolAxios.post(generalAPIRoutes.registeredConsumersCount, payload)

export const registeredConsumersForBabysitterId = ({
  limit,
  page,
  payload,
}: {
  limit: number
  page: number
  payload: { babysitterUserId: string }
}): Promise<commonResponse<registeredConsumersForBabysitterIdResponse>> =>
  anklePatrolAxios.post(
    `${generalAPIRoutes.registeredConsumersForBabysitterId}`,
    payload,
    {
      params: {
        limit,
        page,
      },
    },
  )

export const registerConsumers = ({ queryKey }: QueryFunctionContext) =>
  anklePatrolAxios.get(
    `${generalAPIRoutes.registerConsumers}?limit=${queryKey?.[2]}&page=${queryKey?.[1]}&search=${queryKey?.[3]}`,
  )

export const consumersDetails = (
  payload: ConsumersPayload,
): Promise<commonResponse<ConsumersDetails>> =>
  anklePatrolAxios.post(
    `${generalAPIRoutes.consumersDetails + payload.consumerId}?limit=${payload.limit}&page=${payload.page}`,
    payload,
  )

export const checkInCount = (
  payload: ConsumersPayload,
): Promise<commonResponse<ConsumerUser>> =>
  anklePatrolAxios.post(
    `${generalAPIRoutes.checkInCount + payload.consumerId}/checkIn-count`,
    payload,
  )

export const faceAuthentication = (payload: { id: string }) =>
  anklePatrolAxios.put(generalAPIRoutes.manulfaceAuthentication + payload.id)

export const blockConsumer = (
  payload: BlockConsumer,
): Promise<commonResponse<null>> =>
  anklePatrolAxios.post(generalAPIRoutes.blockConsumer, payload)

export const adminReplyToCloseTicket = (payload: {
  ticketId: string | undefined
  responseText: string
}) => anklePatrolAxios.put(generalAPIRoutes.adminReplyToCloseTicket, payload)

export const uploadFileForGroupChat = (payload: FormData) =>
  anklePatrolAxios.post(generalAPIRoutes.imageUploadInGroupChat, payload)

export interface SubAdminForModal {
  _id: string
  email: string
  role: string
  isAccountBlocked: boolean
  profileInfo: {
    firstName: string
    lastName: string
    profilePic?: string
    countryCode?: string
    phone?: number
  }
}

export interface SubAdminListForModalResponse {
  subAdminProfiles: SubAdminForModal[]
  totalCount: number
  limit: number
  page: number
}

export const fetchSubAdminsForGroupModal = (): Promise<{
  data: SubAdminListForModalResponse
}> =>
  anklePatrolAxios.get(
    `${generalAPIRoutes.subAdminListAll}?limit=200&page=1&search=`,
  )

export const addSubAdminParticipantsToGroup = (
  groupId: string,
  payload: { subAdminUserIds: string[] },
): Promise<{ data: { addedUserIds: string[]; skippedUserIds: string[] } }> =>
  anklePatrolAxios.post(
    `/api/v1/group/${groupId}/sub-admin-participants`,
    payload,
  )

export const removeSubAdminParticipantFromGroup = (
  groupId: string,
  userId: string,
): Promise<{ data: { removedUserId: string } }> =>
  anklePatrolAxios.delete(
    `/api/v1/group/${groupId}/sub-admin-participants/${userId}`,
  )

export interface SupervisorInfo {
  userId: string
  email: string
  role: string
  firstName: string
  lastName: string
  profilePic: string
  phone: number
  countryCode: string
}

export interface ParticipantSupervisorResponse {
  supervisor: SupervisorInfo | null
}

export const fetchParticipantSupervisor = (
  consumerId: string,
): Promise<{ data: ParticipantSupervisorResponse }> =>
  anklePatrolAxios.get(
    `${generalAPIRoutes.participantSupervisor}/${consumerId}`,
  )

export const changeParticipantSupervisor = (payload: {
  consumerId: string
  newSupervisorUserId: string
}): Promise<{ data: { consumerId: string; newSupervisorUserId: string } }> =>
  anklePatrolAxios.put(generalAPIRoutes.changeParticipantSupervisor, payload)

export const fetchAdminRandomCheckIn = ({
  limit,
  page,
  payload,
}: {
  limit?: number
  page?: number
  payload: FetchAdminRandomCheckInPayload
}): Promise<commonResponse<FetchAdminRandomCheckInResponse>> =>
  anklePatrolAxios.post(
    `${generalAPIRoutes.fetchAdminRandomCheckIn}`,
    payload,
    {
      params: {
        ...(limit !== undefined && { limit }),
        ...(page !== undefined && { page }),
      },
    },
  )

export const adminRandomCheckInButtonStatus = (): Promise<
  commonResponse<AdminRandomCheckInButtonStatusResponse>
> => anklePatrolAxios.get(generalAPIRoutes.adminRandomCheckInButtonStatus)

export const AdminImposedCheckIn = (
  payload: AdminImposedCheckInPayload,
): Promise<commonResponse<null>> =>
  anklePatrolAxios.post(generalAPIRoutes.adminImposedCheckIn, payload)
